import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
    Box,
    Typography,
    Button,
    Chip,
    Switch,
    FormControlLabel,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Checkbox,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from '@mui/material';
import { Redirect, useLocation, useHistory } from 'react-router-dom';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { useMutation } from 'react-query';

import TicketField from 'src/components/TicketField';
import { WithoutHeader, DesktopLayout } from 'src/layout';
import getPeriodsInfo from 'src/utils/getPeriodInfo';
import useIsMobileSize from 'src/hooks/useIsMobileSize';
import useSubscriptionsStore from 'src/stores/subscriptions';
import useUserStore from 'src/stores/user';
import useProjectsStore from 'src/stores/projects';
import useCollectionsStore from 'src/stores/collections';
import SubscriptionsService from 'src/services/SubscriptionsService';

import { ROUTES_URL } from 'src/constants/routes';
import LoadingWrapper from 'src/components/Loading/LoadingWrapper';
import TitleWithCounter from 'src/widgets/TitleWithCounter';
import ArrowLeft from 'src/icons/ArrowLeft';
import Wallet from 'src/icons/Wallet';
import ExpandMoreIcon from 'src/icons/ExpandMore';
import ExpandLessIcon from 'src/icons/ExpandLess';
import hexContrastColor from 'hex-contrast-color';
import SubscriptionItem from 'src/widgets/SubscriptionItem';
import { AIIcon } from 'src/icons/AIIcon';

import SubscriptionsForm from 'src/widgets/SubscriptionsForm';

import formatNumber from 'src/utils/formatNumber';
import { getWordWithNumber } from 'src/utils';
import MapLayer from 'src/icons/MapLayer';
import Peoples from 'src/icons/Peoples';
import Dialog from 'src/icons/Dialog';
import Book from 'src/icons/Book';

import CustomPlanConfigurator from './components/CustomPlanConfigurator';
import Points from './components/Points';
import PaymentMethods from './components/PaymentMethods';
import PeriodField from './components/PeriodField';

import type { AxiosError } from 'axios';

type PaymentForm = {
    plan_id: number;
    duration_months: number;
    paymentMethod: string;
    bonus: boolean;
    promo_code?: string;
    auto_renewal: boolean;
};

export default function PaymentScreen() {
    const { enqueueSnackbar } = useSnackbar();
    const history = useHistory();
    const { state } = useLocation();
    const isMobile = useIsMobileSize();

    const [recommendedPlanId, setRecommendedPlanId] = useState<number | null>(null);

    // Состояние AI анализа для индивидуального тарифа
    const [customPlanAIAnalysis, setCustomPlanAIAnalysis] = useState(true);

    // Состояние выгрузки лидов для индивидуального тарифа
    const [customPlanLeadsExport, setCustomPlanLeadsExport] = useState(true);

    // Состояние параметров индивидуального тарифа (отдельно от projectParams)
    const [customPlanParams, setCustomPlanParams] = useState({
        projects: 1,
        keywords: 0,
        bundles: 0,
        chats: 0,
    });

    // Состояние развернутости блока параметров мониторинга
    const [isMonitoringParamsExpanded, setIsMonitoringParamsExpanded] = useState(true);

    // Настройки индивидуального тарифа
    const [customPlanSettings, setCustomPlanSettings] = useState<{
        base_price: number;
        project_price: number;
        keyword_price: number;
        bundle_price: number;
        chat_price: number;
        ai_analysis_price: number;
    } | null>(null);

    const { mutate: createPayment, isLoading: isCreatePaymentLoading } = useMutation(
        SubscriptionsService.queryKey,
        SubscriptionsService.createPayment,
        {
            onSuccess: (data) => {
                document.location = data.confirmation_url;
            },
            onError: (error: any) => {
                console.error('Ошибка создания платежа:', error);
                console.error('Детали ошибки:', error.response?.data);
                enqueueSnackbar('Произошла ошибка при создании платежа. Попробуйте повторить позднее', {
                    variant: 'error',
                });
            },
        }
    );

    const { selectedPlan, promoSale, subscriptions, setSelectedPlan, setSubscriptions } = useSubscriptionsStore(
        ({ selectedPlan, promoSale, subscriptions, setSelectedPlan, setSubscriptions }) => ({
            selectedPlan,
            promoSale,
            subscriptions,
            setSelectedPlan,
            setSubscriptions,
        })
    );

    const { control, handleSubmit, watch, setValue } = useForm<PaymentForm>({
        defaultValues: {
            plan_id: selectedPlan || 1,
            duration_months: (state as { period?: number })?.period || 3,
            paymentMethod: 'UMoney',
            bonus: false,
            promo_code: '',
            auto_renewal: true,
        },
    });

    const { bonusPoints } = useUserStore(({ bonusPoints }) => ({ bonusPoints }));
    const keywordsLimit = useUserStore(({ current_subscription }) => current_subscription?.plan.keywords_limit) || 0;

    // Получаем данные из всех проектов
    const { activeProject, projects } = useProjectsStore(({ activeProject, projects }) => ({
        activeProject,
        projects,
    }));
    const { privateCollection, getPrivateCollection, publicCollections, getCollections } = useCollectionsStore(
        ({ privateCollection, getPrivateCollection, publicCollections, getCollections }) => ({
            privateCollection,
            getPrivateCollection,
            publicCollections,
            getCollections,
        })
    );

    const subscription = subscriptions.filter((s) => s.id === selectedPlan)?.[0];

    // Грузим список подписок при прямом заходе, если его нет
    useEffect(() => {
        if (!subscriptions.length) {
            SubscriptionsService.getSubscriptionsList()
                .then((data) => {
                    setSubscriptions(data);
                })
                .catch(() => {
                    // игнорируем, форма ниже сама обработает пустое состояние
                });
        }
    }, [subscriptions.length, setSubscriptions]);

    // Грузим личную подборку (чаты) при прямом заходе, если она еще не загружена
    const hasLoadedPrivateCollectionRef = useRef(false);
    useEffect(() => {
        if (!activeProject?.id) return;

        // Загружаем только один раз при монтировании компонента
        if (hasLoadedPrivateCollectionRef.current) return;

        // Загружаем, если подборка не загружена (нет id) или если chats не определены
        const hasId = privateCollection?.id !== undefined;
        const hasChats = Array.isArray(privateCollection?.chats);
        const isPrivateCollectionLoaded = hasId && hasChats;

        if (!isPrivateCollectionLoaded) {
            hasLoadedPrivateCollectionRef.current = true;
            getPrivateCollection()
                .then((loaded) => {
                    console.log('✅ Личная подборка загружена, чаты:', loaded?.chats?.length || 0);
                })
                .catch((error) => {
                    console.error('❌ Ошибка загрузки личной подборки:', error);
                    hasLoadedPrivateCollectionRef.current = false; // Разрешаем повторную попытку при ошибке
                });
        } else {
            hasLoadedPrivateCollectionRef.current = true;
        }
    }, [activeProject?.id]); // Убрали зависимости от privateCollection, чтобы избежать бесконечного цикла

    // Грузим настройки индивидуального тарифа
    useEffect(() => {
        if (!customPlanSettings) {
            SubscriptionsService.getCustomPlanSettings()
                .then((data) => {
                    setCustomPlanSettings({
                        base_price: parseFloat(data.base_price),
                        project_price: parseFloat(data.project_price || '0'),
                        keyword_price: parseFloat(data.keyword_price),
                        bundle_price: parseFloat(data.bundle_price),
                        chat_price: parseFloat(data.chat_price),
                        ai_analysis_price: parseFloat(data.ai_analysis_price || '0'),
                    });
                })
                .catch((error: AxiosError) => {
                    console.error('Ошибка загрузки настроек индивидуального тарифа:', error);
                    // Используем значения по умолчанию при ошибке
                    setCustomPlanSettings({
                        base_price: 990,
                        project_price: 0,
                        keyword_price: 200,
                        bundle_price: 300,
                        chat_price: 150,
                        ai_analysis_price: 0,
                    });
                });
        }
    }, [customPlanSettings]);

    // Грузим публичные подборки (для отображения города/количества чатов)
    // Важно: getCollections может перезаписать privateCollection, поэтому вызываем только если нет загруженной подборки
    useEffect(() => {
        if (!publicCollections || publicCollections.length === 0) {
            // Сохраняем текущую privateCollection перед вызовом getCollections
            const currentPrivateCollection = privateCollection;
            getCollections()
                .then(() => {
                    // Восстанавливаем privateCollection если она была загружена
                    if (currentPrivateCollection?.id && currentPrivateCollection?.chats !== undefined) {
                        useCollectionsStore.setState({ privateCollection: currentPrivateCollection });
                    }
                })
                .catch(() => {});
        }
    }, [publicCollections?.length]);

    // Состояние для disabled элементов
    const [disabledKeywords, setDisabledKeywords] = useState<number[]>([]);
    const [disabledBundles, setDisabledBundles] = useState<number[]>([]);
    const [disabledChats, setDisabledChats] = useState<number[]>([]);

    // Получаем данные из ВСЕХ проектов
    const projectData = useMemo(() => {
        // Собираем уникальные публичные подборки из всех проектов
        const bundlesMap = new Map<number, any>();
        const bundlesList: any[] = [];
        const keywordsList: any[] = [];
        let totalChats = 0;

        projects.forEach((project) => {
            if ('bundles' in project && project.bundles) {
                // Публичные подборки (уникальные)
                project.bundles.forEach((bundle) => {
                    if (bundle.is_public && !bundlesMap.has(bundle.id)) {
                        bundlesMap.set(bundle.id, bundle);
                        bundlesList.push(bundle);
                    }
                });

                // Чаты из личной подборки проекта
                const privateBundle = project.bundles.find((bundle) => !bundle.is_public);
                if (privateBundle?.chat_count) {
                    totalChats += privateBundle.chat_count;
                }
            }

            // Ключевые слова из всех проектов
            if ('keywords' in project && project.keywords) {
                keywordsList.push(...project.keywords);
            }
        });

        // Для UI используем чаты из активного проекта (для отображения в списке)
        const activeChats = privateCollection?.chats || [];

        return {
            bundlesList: bundlesList,
            keywordsList: keywordsList,
            chatsList: activeChats.filter((c) => c.id !== undefined), // Для UI используем активный проект
            bundlesCount: bundlesList.filter((b) => !disabledBundles.includes(b.id)).length,
            keywordsCount: keywordsList.filter((k) => !disabledKeywords.includes(k.id)).length,
            chatsCount: totalChats, // Используем общее количество из всех проектов
        };
    }, [projects, activeProject, privateCollection, disabledBundles, disabledKeywords, disabledChats]);

    // Подсчитываем параметры для рекомендации тарифа (из всех проектов)
    const projectParams = useMemo(() => {
        return {
            bundles: projectData.bundlesCount,
            keywords: projectData.keywordsCount,
            chats: projectData.chatsCount,
        };
    }, [projectData]);

    const MAX_KEYWORDS_LIMIT = 100;
    const effectiveKeywordsLimit = Math.min(keywordsLimit || MAX_KEYWORDS_LIMIT, MAX_KEYWORDS_LIMIT);

    // Функция автоматической корректировки выбранных элементов при смене тарифа
    const adjustSelectedItemsForPlan = React.useCallback(
        (plan: (typeof subscriptions)[0] | null) => {
            if (!plan || plan.id === CUSTOM_PLAN_ID) {
                // Для индивидуального тарифа не корректируем
                return;
            }

            // Получаем исходные списки без учета disabled элементов
            const allBundles = projectData.bundlesList;
            const allKeywords = projectData.keywordsList;
            const allChats = projectData.chatsList.filter((c) => c.id !== undefined);

            // Корректируем подборки: оставляем первые N, остальные отключаем
            const allBundleIds = allBundles.map((b) => b.id);
            if (allBundles.length > plan.bundles_limit) {
                const bundlesToKeep = allBundles.slice(0, plan.bundles_limit).map((b) => b.id);
                const bundlesToDisable = allBundles.slice(plan.bundles_limit).map((b) => b.id);
                setDisabledBundles((prev) => {
                    // Убираем из disabled те, которые должны быть включены
                    const filtered = prev.filter((id) => bundlesToKeep.includes(id));
                    // Добавляем те, которые должны быть отключены
                    return Array.from(new Set([...filtered, ...bundlesToDisable]));
                });
            } else {
                // Если все подборки помещаются в лимит, включаем все (убираем из disabled)
                setDisabledBundles((prev) => prev.filter((id) => !allBundleIds.includes(id)));
            }

            // Корректируем ключевые слова: оставляем первые N, остальные отключаем
            const allKeywordIds = allKeywords.map((k) => k.id);
            if (allKeywords.length > plan.keywords_limit) {
                const keywordsToKeep = allKeywords.slice(0, plan.keywords_limit).map((k) => k.id);
                const keywordsToDisable = allKeywords.slice(plan.keywords_limit).map((k) => k.id);
                setDisabledKeywords((prev) => {
                    // Убираем из disabled те, которые должны быть включены
                    const filtered = prev.filter((id) => keywordsToKeep.includes(id));
                    // Добавляем те, которые должны быть отключены
                    return Array.from(new Set([...filtered, ...keywordsToDisable]));
                });
            } else {
                // Если все ключевые слова помещаются в лимит, включаем все (убираем из disabled)
                setDisabledKeywords((prev) => prev.filter((id) => !allKeywordIds.includes(id)));
            }

            // Корректируем чаты: оставляем первые N, остальные отключаем
            const allChatIds = allChats.map((c) => c.id).filter((id): id is number => id !== undefined);
            if (allChats.length > plan.chats_limit) {
                const chatsToKeep = allChats
                    .slice(0, plan.chats_limit)
                    .map((c) => c.id)
                    .filter((id): id is number => id !== undefined);
                const chatsToDisable = allChats
                    .slice(plan.chats_limit)
                    .map((c) => c.id)
                    .filter((id): id is number => id !== undefined);
                setDisabledChats((prev) => {
                    // Убираем из disabled те, которые должны быть включены
                    const filtered = prev.filter((id) => chatsToKeep.includes(id));
                    // Добавляем те, которые должны быть отключены
                    return Array.from(new Set([...filtered, ...chatsToDisable]));
                });
            } else {
                // Если все чаты помещаются в лимит, включаем все (убираем из disabled)
                setDisabledChats((prev) => prev.filter((id) => !allChatIds.includes(id)));
            }
        },
        [projectData.bundlesList, projectData.keywordsList, projectData.chatsList]
    );

    // Расчет стоимости индивидуального тарифа
    const calculateCustomPlanPrice = (): number => {
        if (!customPlanSettings) {
            // Если настройки еще не загружены, возвращаем 0 или можно показать загрузку
            return 0;
        }

        // Используем параметры из конфигуратора индивидуального тарифа
        // Первый проект уже включен в base_price, платим только за дополнительные проекты
        const projectsCost = customPlanSettings.project_price * (customPlanParams.projects - 1);
        const keywordsCost = customPlanParams.keywords * customPlanSettings.keyword_price;
        const bundlesCost = customPlanParams.bundles * customPlanSettings.bundle_price;
        const chatsCost = customPlanParams.chats * customPlanSettings.chat_price;
        // AI analysis включается по свитчеру
        const aiAnalysisCost = customPlanAIAnalysis ? customPlanSettings.ai_analysis_price : 0;

        return customPlanSettings.base_price + projectsCost + keywordsCost + bundlesCost + chatsCost + aiAnalysisCost;
    };

    // Проверяем, выбран ли индивидуальный тариф (используем специальный ID)
    const CUSTOM_PLAN_ID = -1;

    // Пересчитываем цену индивидуального тарифа при изменении параметров или AI анализа
    const customPlanPrice = useMemo(
        () => calculateCustomPlanPrice(),
        [
            customPlanSettings,
            customPlanParams.projects,
            customPlanParams.keywords,
            customPlanParams.bundles,
            customPlanParams.chats,
            customPlanAIAnalysis,
        ]
    );

    // Инициализируем параметры индивидуального тарифа из projectParams (из всех проектов) при первом рендере
    useEffect(() => {
        if (customPlanParams.keywords === 0 && customPlanParams.bundles === 0 && customPlanParams.chats === 0) {
            setCustomPlanParams({
                projects: Math.max(1, projects.length),
                keywords: projectParams.keywords,
                bundles: projectParams.bundles,
                chats: projectParams.chats,
            });
        }
    }, [projectParams.keywords, projectParams.bundles, projectParams.chats, projects.length]);

    // Синхронизируем customPlanParams с projectParams (из всех проектов): значения не могут быть меньше projectParams
    // Также обновляем при изменении disabledKeywords, disabledBundles, disabledChats (свитчеры)
    useEffect(() => {
        setCustomPlanParams((prev) => ({
            ...prev,
            projects: Math.max(prev.projects, projects.length),
            // Обновляем параметры на основе актуальных projectParams (учитывая disabled элементы)
            keywords: Math.max(prev.keywords, projectParams.keywords),
            bundles: Math.max(prev.bundles, projectParams.bundles),
            chats: Math.max(prev.chats, projectParams.chats),
        }));
    }, [
        projectParams.keywords,
        projectParams.bundles,
        projectParams.chats,
        projects.length,
        disabledKeywords,
        disabledBundles,
        disabledChats,
    ]);

    // Индивидуальный тариф всегда показываем, если есть хотя бы один параметр
    const shouldShowCustomPlan = projectParams.keywords > 0 || projectParams.bundles > 0 || projectParams.chats > 0;

    // Автоматическая корректировка выбранных элементов при смене тарифа
    useEffect(() => {
        if (!subscriptions.length || selectedPlan === CUSTOM_PLAN_ID) return;

        const currentPlan = subscriptions.find((p) => p.id === selectedPlan);
        if (currentPlan) {
            adjustSelectedItemsForPlan(currentPlan);
        }
    }, [selectedPlan, subscriptions, adjustSelectedItemsForPlan]);

    const handlePlanChange = (planId: number) => {
        setSelectedPlan(planId);
        setValue('plan_id', planId);
        // Автоматически корректируем выбранные элементы при смене тарифа
        if (planId !== CUSTOM_PLAN_ID && subscriptions.length > 0) {
            const selectedPlanObj = subscriptions.find((p) => p.id === planId);
            if (selectedPlanObj) {
                adjustSelectedItemsForPlan(selectedPlanObj);
            }
        }
    };

    // Обработчик изменения тарифа через Radio
    const [selectedTariff, setSelectedTariff] = useState<number>(selectedPlan);

    // Логика рекомендации тарифа (рекомендуем самый дешевый тариф, который покрывает выбранные параметры)
    useEffect(() => {
        if (!subscriptions.length) return;

        // Находим подходящий тариф на основе выбранных параметров
        // Сначала проверяем, какие тарифы покрывают все выбранные параметры
        const recommended = subscriptions
            .filter((plan) => {
                // Тариф подходит, если его лимиты >= выбранным параметрам
                return (
                    plan.bundles_limit >= projectParams.bundles &&
                    plan.keywords_limit >= projectParams.keywords &&
                    plan.chats_limit >= projectParams.chats
                );
            })
            .sort((a, b) => a.price - b.price)[0];

        setRecommendedPlanId(recommended?.id || null);

        // Устанавливаем рекомендуемый тариф по умолчанию только при первой загрузке (если тариф еще не выбран)
        if (recommended?.id && !selectedPlan) {
            setSelectedPlan(recommended.id);
            setValue('plan_id', recommended.id);
        }
    }, [subscriptions, projectParams]);

    useEffect(() => {
        if (selectedTariff !== selectedPlan) {
            handlePlanChange(selectedTariff);
        }
    }, [selectedTariff]);

    useEffect(() => {
        if (subscription && subscription.id) {
            setValue('plan_id', subscription.id);
        }
    }, [subscription?.id]);

    // Убрали проверку !subscription - всегда показываем полную страницу payment
    // Пользователь может выбрать тариф прямо на странице оплаты

    const isCustomPlanSelected = selectedPlan === CUSTOM_PLAN_ID;
    // Для расчета используем цену индивидуального тарифа если он выбран, иначе цену subscription или 0
    const currentPlanPrice = isCustomPlanSelected
        ? customPlanPrice
        : subscription?.price ?? (subscriptions.length > 0 ? subscriptions[0]?.price ?? 0 : 0);

    const periods = Object.values(getPeriodsInfo(currentPlanPrice));
    const periodId = watch('duration_months');
    const isUseBonus = watch('bonus');
    const period = periods.filter((item) => item.value === periodId)?.[0];
    let total = period?.priceWithSale || 0;
    let promocodeDiff = 0;

    if (promoSale > 0) {
        promocodeDiff = total;
        total = total - (total / 100) * promoSale;
        promocodeDiff = promocodeDiff - total;
    }

    // Вычисляем сумму до применения бонусов для ограничения
    const totalBeforeBonus = total;

    // Ограничиваем использование бонусов до суммы к оплате
    const actualBonusToUse = isUseBonus ? Math.min(bonusPoints, totalBeforeBonus) : 0;

    if (isUseBonus) {
        total = Math.max(0, total - actualBonusToUse);
    }

    const onSubmit: SubmitHandler<PaymentForm> = (data) => {
        const body: any = {
            duration_months: data.duration_months,
            is_recurring: data.auto_renewal,
        };

        // Если выбран индивидуальный тариф
        if (data.plan_id === CUSTOM_PLAN_ID) {
            body['custom_plan'] = true;
            body['projects_count'] = customPlanParams.projects;
            body['keywords_count'] = customPlanParams.keywords;
            body['bundles_count'] = customPlanParams.bundles;
            body['chats_count'] = customPlanParams.chats;
            body['ai_analysis_enabled'] = customPlanAIAnalysis;
            body['price'] = customPlanPrice;
        } else {
            body['plan_id'] = data.plan_id;
        }

        if (data.promo_code && data.promo_code.trim() && promoSale > 0) {
            body['promo_code'] = data.promo_code.toUpperCase();
        }

        if (data.bonus) {
            // Вычисляем сумму до применения бонусов (аналогично расчету выше)
            let totalBeforeBonus = period?.priceWithSale || 0;
            if (promoSale > 0) {
                totalBeforeBonus = totalBeforeBonus - (totalBeforeBonus / 100) * promoSale;
            }
            // Отправляем только допустимое количество бонусов (не больше суммы к оплате)
            const actualBonusToUse = Math.min(bonusPoints, totalBeforeBonus);
            body['use_bonus_points'] = actualBonusToUse;
        }

        console.log('Отправляем данные на оплату:', body);
        console.log('Рассчитанная итоговая сумма:', total);
        console.log('Промокод из формы:', data.promo_code);
        console.log('PromoSale из store:', promoSale);
        console.log('Используются бонусы:', data.bonus);
        console.log('Количество бонусов:', bonusPoints);
        console.log('Plan ID:', data.plan_id);
        console.log('Duration months:', data.duration_months);
        console.log('Все данные формы:', data);
        console.log('Условие для промокода:', data.promo_code && data.promo_code.trim() && promoSale > 0);
        console.log(
            'Промокод будет отправлен:',
            data.promo_code && data.promo_code.trim() && promoSale > 0 ? data.promo_code : 'НЕТ'
        );
        createPayment(body);
    };

    const renderContent = () => {
        return (
            <LoadingWrapper isLoading={isCreatePaymentLoading}>
                <Box
                    display="flex"
                    flexDirection="column"
                    minHeight="100%"
                    flexGrow={1}
                >
                    <Box mb={3}>
                        <Typography
                            variant="h1"
                            color="primary"
                        >
                            Запуск сервиса
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: 'rgba(0, 0, 0, 0.7)',
                                fontSize: 16,
                                fontWeight: 400,
                                mt: 1.5,
                                lineHeight: 1.5,
                                maxWidth: '800px',
                            }}
                        >
                            Выберите подходящий тариф на нужный срок — и запустите сервис, который будет находить
                            клиентов для вас 24/7
                        </Typography>
                    </Box>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Box>
                            {/* Параметры мониторинга */}
                            <Box mb={3}>
                                <Accordion
                                    expanded={isMonitoringParamsExpanded}
                                    onChange={(_, expanded) => setIsMonitoringParamsExpanded(expanded)}
                                    sx={{
                                        boxShadow: 'none',
                                        border: 'none',
                                        '&:before': { display: 'none' },
                                        backgroundColor: 'transparent',
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={
                                            isMonitoringParamsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />
                                        }
                                        sx={{
                                            px: 0,
                                            minHeight: 'auto',
                                            '& .MuiAccordionSummary-content': {
                                                margin: '0 !important',
                                            },
                                            '& .MuiAccordionSummary-expandIconWrapper': {
                                                color: 'primary.main',
                                                '& svg': {
                                                    width: '24px',
                                                    height: '24px',
                                                },
                                            },
                                            '&.Mui-expanded': {
                                                minHeight: 'auto',
                                            },
                                        }}
                                    >
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            width="100%"
                                            pr={2}
                                        >
                                            <Typography
                                                variant="h5"
                                                color="primary"
                                                sx={{
                                                    fontWeight: 800,
                                                    fontSize: 24,
                                                    lineHeight: 1.2,
                                                }}
                                            >
                                                Параметры мониторинга
                                            </Typography>
                                            {!isMonitoringParamsExpanded && (
                                                <Box
                                                    display="flex"
                                                    gap={1.5}
                                                    alignItems="center"
                                                >
                                                    {projectData.keywordsList.length > 0 && (
                                                        <Chip
                                                            size="small"
                                                            label={getWordWithNumber(projectParams.keywords, [
                                                                'ключевое слово',
                                                                'ключевых слова',
                                                                'ключевых слов',
                                                            ])}
                                                            sx={{ fontWeight: 600 }}
                                                        />
                                                    )}
                                                    {projectData.bundlesList.length > 0 && (
                                                        <Chip
                                                            size="small"
                                                            label={getWordWithNumber(projectParams.bundles, [
                                                                'подборка',
                                                                'подборки',
                                                                'подборок',
                                                            ])}
                                                            sx={{ fontWeight: 600 }}
                                                        />
                                                    )}
                                                    {projectData.chatsList.length > 0 && (
                                                        <Chip
                                                            size="small"
                                                            label={getWordWithNumber(projectParams.chats, [
                                                                'чат',
                                                                'чата',
                                                                'чатов',
                                                            ])}
                                                            sx={{ fontWeight: 600 }}
                                                        />
                                                    )}
                                                </Box>
                                            )}
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ px: 0, pb: 0 }}>
                                        <Box
                                            sx={{
                                                maxHeight: isMonitoringParamsExpanded ? '600px' : 'none',
                                                overflowY: 'auto',
                                                pr: 1,
                                                '&::-webkit-scrollbar': {
                                                    width: '6px',
                                                },
                                                '&::-webkit-scrollbar-track': {
                                                    background: '#f1f1f1',
                                                    borderRadius: '3px',
                                                },
                                                '&::-webkit-scrollbar-thumb': {
                                                    background: '#c1c1c1',
                                                    borderRadius: '3px',
                                                },
                                            }}
                                        >
                                            {/* Ключевые слова (стиль как на шаге Keywords) */}
                                            {projectData.keywordsList.length > 0 && (
                                                <Box mb={2}>
                                                    <Box
                                                        display="flex"
                                                        alignItems="center"
                                                        justifyContent="space-between"
                                                        mb={1.5}
                                                    >
                                                        <Typography
                                                            variant="body1"
                                                            color="primary"
                                                            sx={{ fontWeight: 600 }}
                                                        >
                                                            Ключевые слова:
                                                        </Typography>
                                                        {projectData.keywordsList.length > 0 && (
                                                            <Chip
                                                                color="primary"
                                                                variant={
                                                                    projectParams.keywords ===
                                                                    projectData.keywordsList.length
                                                                        ? 'filled'
                                                                        : 'outlined'
                                                                }
                                                                label={`${projectParams.keywords} из ${projectData.keywordsList.length}`}
                                                                sx={{
                                                                    background:
                                                                        projectParams.keywords !==
                                                                            projectData.keywordsList.length && !isMobile
                                                                            ? '#fff'
                                                                            : null,
                                                                }}
                                                            />
                                                        )}
                                                    </Box>
                                                    <List
                                                        style={{
                                                            border: '1px solid',
                                                            borderColor: '#E3ECF3',
                                                            borderRadius: '16px',
                                                            backgroundColor: '#fff',
                                                            width: '100%',
                                                        }}
                                                        disablePadding
                                                    >
                                                        {projectData.keywordsList.map((keyword, idx) => {
                                                            const id = keyword.id;
                                                            const name = keyword.word as string;
                                                            const isSelected = !disabledKeywords.includes(id);
                                                            const rawAI = activeProject?.niche_analysis_keywords;
                                                            const aiKeywords: string[] = Array.isArray(rawAI)
                                                                ? rawAI
                                                                : [];
                                                            const isAIRecommended = name
                                                                ? aiKeywords.includes(name)
                                                                : false;

                                                            return (
                                                                <ListItem
                                                                    key={id}
                                                                    divider={
                                                                        projectData.keywordsList.length - 1 !== idx
                                                                    }
                                                                    secondaryAction={
                                                                        <Switch
                                                                            checked={isSelected}
                                                                            onChange={() => {
                                                                                setDisabledKeywords((prev) =>
                                                                                    isSelected
                                                                                        ? [...prev, id]
                                                                                        : prev.filter((k) => k !== id)
                                                                                );
                                                                            }}
                                                                            color="primary"
                                                                        />
                                                                    }
                                                                >
                                                                    <ListItemIcon>
                                                                        <Box
                                                                            sx={{
                                                                                '& svg': {
                                                                                    width: '20px',
                                                                                    height: '20px',
                                                                                },
                                                                            }}
                                                                        >
                                                                            <Book />
                                                                        </Box>
                                                                    </ListItemIcon>
                                                                    <ListItemText
                                                                        primary={
                                                                            <Box
                                                                                display="flex"
                                                                                alignItems="center"
                                                                                gap={1}
                                                                            >
                                                                                <Typography variant="body1">
                                                                                    {name}
                                                                                </Typography>
                                                                                {isAIRecommended && (
                                                                                    <Chip
                                                                                        icon={
                                                                                            <AIIcon
                                                                                                width={14}
                                                                                                height={14}
                                                                                            />
                                                                                        }
                                                                                        label="ИИ рекомендует"
                                                                                        size="small"
                                                                                        sx={{
                                                                                            backgroundColor:
                                                                                                'rgba(122, 48, 228, 0.1)',
                                                                                            color: '#7A30E4',
                                                                                            fontWeight: 600,
                                                                                            border: '1px solid rgba(122, 48, 228, 0.2)',
                                                                                            '& .MuiChip-icon': {
                                                                                                color: '#7A30E4',
                                                                                            },
                                                                                        }}
                                                                                    />
                                                                                )}
                                                                            </Box>
                                                                        }
                                                                    />
                                                                </ListItem>
                                                            );
                                                        })}
                                                    </List>
                                                </Box>
                                            )}

                                            {/* Подборки (как на Collections) */}
                                            {projectData.bundlesList.length > 0 && (
                                                <Box mb={2}>
                                                    <Box
                                                        display="flex"
                                                        alignItems="center"
                                                        justifyContent="space-between"
                                                        mb={1.5}
                                                    >
                                                        <Typography
                                                            variant="body1"
                                                            color="primary"
                                                            sx={{ fontWeight: 600 }}
                                                        >
                                                            Подборки чатов:
                                                        </Typography>
                                                        <Chip
                                                            color="primary"
                                                            variant={
                                                                projectParams.bundles === projectData.bundlesList.length
                                                                    ? 'filled'
                                                                    : 'outlined'
                                                            }
                                                            label={`${projectParams.bundles} из ${projectData.bundlesList.length}`}
                                                            sx={{
                                                                background:
                                                                    projectParams.bundles !==
                                                                        projectData.bundlesList.length && !isMobile
                                                                        ? '#fff'
                                                                        : null,
                                                            }}
                                                        />
                                                    </Box>

                                                    <List
                                                        sx={{
                                                            width: '100%',
                                                            background: '#fff',
                                                            border: '1px solid',
                                                            borderColor: '#E3ECF3',
                                                            display: 'block',
                                                            borderRadius: '16px',
                                                        }}
                                                        disablePadding
                                                    >
                                                        {projectData.bundlesList.map((bundle, idx) => {
                                                            const isSelected = !disabledBundles.includes(bundle.id);
                                                            const recommendedCollectionIds =
                                                                activeProject?.recommended_collections || [];
                                                            const isAIRecommended = recommendedCollectionIds.includes(
                                                                bundle.id
                                                            );
                                                            const bundleDetail = publicCollections?.find(
                                                                (c) => c.id === bundle.id
                                                            );

                                                            return (
                                                                <ListItem
                                                                    key={bundle.id}
                                                                    divider={idx !== projectData.bundlesList.length - 1}
                                                                    secondaryAction={
                                                                        <Switch
                                                                            checked={isSelected}
                                                                            onChange={() => {
                                                                                setDisabledBundles((prev) =>
                                                                                    isSelected
                                                                                        ? [...prev, bundle.id]
                                                                                        : prev.filter(
                                                                                              (id) => id !== bundle.id
                                                                                          )
                                                                                );
                                                                            }}
                                                                            color="primary"
                                                                        />
                                                                    }
                                                                >
                                                                    <ListItemIcon>
                                                                        <MapLayer />
                                                                    </ListItemIcon>
                                                                    <ListItemText
                                                                        primary={
                                                                            <Box
                                                                                display="flex"
                                                                                alignItems="center"
                                                                                gap={1}
                                                                            >
                                                                                <Typography
                                                                                    variant="body1"
                                                                                    color="primary"
                                                                                >
                                                                                    {bundleDetail?.name ||
                                                                                        bundle.name ||
                                                                                        `Подборка ${bundle.id}`}
                                                                                </Typography>
                                                                                {isAIRecommended && (
                                                                                    <Chip
                                                                                        icon={
                                                                                            <AIIcon
                                                                                                width={14}
                                                                                                height={14}
                                                                                            />
                                                                                        }
                                                                                        label="ИИ рекомендует"
                                                                                        size="small"
                                                                                        sx={{
                                                                                            backgroundColor:
                                                                                                'rgba(122, 48, 228, 0.1)',
                                                                                            color: '#7A30E4',
                                                                                            fontWeight: 600,
                                                                                            border: '1px solid rgba(122, 48, 228, 0.2)',
                                                                                            '& .MuiChip-icon': {
                                                                                                color: '#7A30E4',
                                                                                            },
                                                                                        }}
                                                                                    />
                                                                                )}
                                                                            </Box>
                                                                        }
                                                                        secondary={
                                                                            <Box
                                                                                display="flex"
                                                                                alignItems="center"
                                                                                gap={1}
                                                                            >
                                                                                {(bundleDetail?.city ||
                                                                                    bundleDetail?.location) && (
                                                                                    <Typography
                                                                                        variant="body2"
                                                                                        color="primary"
                                                                                        sx={{
                                                                                            fontWeight: 500,
                                                                                            backgroundColor:
                                                                                                'rgba(0, 136, 204, 0.1)',
                                                                                            padding: '2px 8px',
                                                                                            borderRadius: '12px',
                                                                                            border: '1px solid rgba(0, 136, 204, 0.2)',
                                                                                        }}
                                                                                    >
                                                                                        {bundleDetail?.city ||
                                                                                            bundleDetail?.location}
                                                                                    </Typography>
                                                                                )}
                                                                                {(bundleDetail?.chat_count ??
                                                                                    bundle.chat_count) !==
                                                                                    undefined && (
                                                                                    <Box
                                                                                        display="flex"
                                                                                        alignItems="center"
                                                                                        gap={1}
                                                                                    >
                                                                                        <Peoples />
                                                                                        <Typography
                                                                                            variant="body2"
                                                                                            color="primary"
                                                                                        >
                                                                                            {getWordWithNumber(
                                                                                                (bundleDetail?.chat_count ??
                                                                                                    bundle.chat_count) as number,
                                                                                                ['чат', 'чата', 'чатов']
                                                                                            )}
                                                                                        </Typography>
                                                                                    </Box>
                                                                                )}
                                                                            </Box>
                                                                        }
                                                                    />
                                                                </ListItem>
                                                            );
                                                        })}
                                                    </List>
                                                </Box>
                                            )}

                                            {/* Чаты (как ключевые слова и подборки) */}
                                            {projectData.chatsList.length > 0 && (
                                                <Box mb={2}>
                                                    <Box
                                                        display="flex"
                                                        alignItems="center"
                                                        justifyContent="space-between"
                                                        mb={1.5}
                                                    >
                                                        <Typography
                                                            variant="body1"
                                                            color="primary"
                                                            sx={{ fontWeight: 600 }}
                                                        >
                                                            Добавленные вами чаты:
                                                        </Typography>
                                                        <Chip
                                                            color="primary"
                                                            variant={
                                                                projectParams.chats === projectData.chatsList.length
                                                                    ? 'filled'
                                                                    : 'outlined'
                                                            }
                                                            label={`${projectParams.chats} из ${projectData.chatsList.length}`}
                                                            sx={{
                                                                background:
                                                                    projectParams.chats !==
                                                                        projectData.chatsList.length && !isMobile
                                                                        ? '#fff'
                                                                        : null,
                                                            }}
                                                        />
                                                    </Box>
                                                    <List
                                                        sx={{
                                                            width: '100%',
                                                            background: '#fff',
                                                            border: '1px solid',
                                                            borderColor: '#E3ECF3',
                                                            display: 'block',
                                                            borderRadius: '16px',
                                                        }}
                                                        disablePadding
                                                    >
                                                        {projectData.chatsList.map((chat, idx) => {
                                                            const chatId = chat.id;
                                                            if (chatId === undefined) return null;
                                                            const isSelected = !disabledChats.includes(chatId);
                                                            const chatName =
                                                                chat.name || chat.telegram_link || `Чат ${chatId}`;

                                                            return (
                                                                <ListItem
                                                                    key={chatId}
                                                                    divider={idx !== projectData.chatsList.length - 1}
                                                                    secondaryAction={
                                                                        <Switch
                                                                            checked={isSelected}
                                                                            onChange={() => {
                                                                                setDisabledChats((prev) =>
                                                                                    isSelected
                                                                                        ? [...prev, chatId]
                                                                                        : prev.filter(
                                                                                              (id) => id !== chatId
                                                                                          )
                                                                                );
                                                                            }}
                                                                            color="primary"
                                                                        />
                                                                    }
                                                                >
                                                                    <ListItemIcon>
                                                                        <Box
                                                                            sx={{
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center',
                                                                                '& svg': {
                                                                                    width: '24px',
                                                                                    height: '24px',
                                                                                },
                                                                            }}
                                                                        >
                                                                            <Dialog />
                                                                        </Box>
                                                                    </ListItemIcon>
                                                                    <ListItemText
                                                                        primary={
                                                                            <Typography
                                                                                variant="body1"
                                                                                color="primary"
                                                                            >
                                                                                {chatName}
                                                                            </Typography>
                                                                        }
                                                                    />
                                                                </ListItem>
                                                            );
                                                        })}
                                                    </List>
                                                </Box>
                                            )}
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                            </Box>

                            {/* Визуальный разделитель после параметров мониторинга */}
                            <Box
                                sx={{
                                    height: '1px',
                                    backgroundColor: '#E3ECF3',
                                    my: 4,
                                    width: '100%',
                                }}
                            />

                            {/* Все доступные тарифы */}
                            <Box mb={4}>
                                <Typography
                                    variant="h5"
                                    color="primary"
                                    sx={{
                                        fontWeight: 800,
                                        fontSize: 24,
                                        mb: 2.5,
                                        lineHeight: 1.2,
                                    }}
                                >
                                    Выберите тариф
                                </Typography>
                                <Box
                                    display="grid"
                                    gridTemplateColumns={isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))'}
                                    gap={2}
                                >
                                    {subscriptions
                                        .sort((a, b) => {
                                            if (+a.price < +b.price) return -1;
                                            if (+a.price > +b.price) return 1;
                                            return 0;
                                        })
                                        .map((plan) => {
                                            const isRecommended = plan.id === recommendedPlanId;
                                            // Рассчитываем цену за месяц с учетом выбранного периода
                                            const planPeriods = Object.values(getPeriodsInfo(plan.price));
                                            const planPeriod = planPeriods.find((p) => p.value === periodId);
                                            const monthlyPriceWithDiscount = planPeriod
                                                ? planPeriod.priceWithSale / periodId
                                                : plan.price;

                                            return (
                                                <Box
                                                    key={plan.id}
                                                    sx={{ position: 'relative', height: '100%' }}
                                                >
                                                    {isRecommended && (
                                                        <Box
                                                            sx={{
                                                                position: 'absolute',
                                                                top: -12,
                                                                left: '50%',
                                                                transform: 'translateX(-50%)',
                                                                zIndex: 1,
                                                                pointerEvents: 'none',
                                                            }}
                                                        >
                                                            <Chip
                                                                icon={
                                                                    <AIIcon
                                                                        width={14}
                                                                        height={14}
                                                                    />
                                                                }
                                                                label="ИИ рекомендует"
                                                                size="small"
                                                                sx={{
                                                                    backgroundColor: 'rgba(122, 48, 228, 0.1)',
                                                                    color: '#7A30E4',
                                                                    fontWeight: 600,
                                                                    border: '1px solid rgba(122, 48, 228, 0.2)',
                                                                    '& .MuiChip-icon': {
                                                                        color: '#7A30E4',
                                                                    },
                                                                }}
                                                            />
                                                        </Box>
                                                    )}
                                                    <SubscriptionItem
                                                        value={plan.id}
                                                        checkedValue={selectedPlan}
                                                        title={plan.name}
                                                        price={plan.price}
                                                        color={'#' + plan.color_code}
                                                        projects={plan.projects_limit}
                                                        bundles={plan.bundles_limit}
                                                        chats={plan.chats_limit}
                                                        keywords={plan.keywords_limit}
                                                        ai_analysis_enabled={plan.ai_analysis_enabled}
                                                        spam_filter_enabled={plan.spam_filter_enabled}
                                                        telegram_notifications_enabled={
                                                            plan.telegram_notifications_enabled
                                                        }
                                                        leads_export_enabled={plan.leads_export_enabled}
                                                        setup_service_enabled={plan.setup_service_enabled}
                                                        description={plan.description}
                                                        isRecommended={isRecommended}
                                                        potential_clients_per_month={plan.potential_clients_per_month}
                                                        monthlyPriceWithDiscount={monthlyPriceWithDiscount}
                                                        selectedDurationMonths={periodId}
                                                        onChange={(val) => {
                                                            const planId = Number(val);
                                                            setSelectedTariff(planId);
                                                            handlePlanChange(planId);
                                                        }}
                                                    />
                                                </Box>
                                            );
                                        })}

                                    {/* Индивидуальный тариф */}
                                    {shouldShowCustomPlan && (
                                        <Box
                                            sx={{
                                                position: 'relative',
                                                height: '100%',
                                                gridColumn: isMobile ? '1' : '1 / -1',
                                            }}
                                        >
                                            <SubscriptionItem
                                                value={CUSTOM_PLAN_ID}
                                                checkedValue={selectedPlan}
                                                title="Индивидуальный"
                                                price={customPlanPrice || 0}
                                                color="#0044FF"
                                                projects={customPlanParams.projects}
                                                bundles={customPlanParams.bundles}
                                                chats={customPlanParams.chats}
                                                keywords={customPlanParams.keywords}
                                                ai_analysis_enabled={false}
                                                spam_filter_enabled={true}
                                                telegram_notifications_enabled={true}
                                                leads_export_enabled={customPlanLeadsExport}
                                                setup_service_enabled={false}
                                                description="Тариф формируется на основе выбранных вами параметров. Все опции доступны, кроме настройки под ключ."
                                                isRecommended={false}
                                                disabled={false}
                                                isCustomPlan={true}
                                                customPlanAIAnalysis={customPlanAIAnalysis}
                                                onCustomPlanAIAnalysisChange={setCustomPlanAIAnalysis}
                                                aiAnalysisPrice={customPlanSettings?.ai_analysis_price || 0}
                                                customPlanConfigurator={
                                                    customPlanSettings ? (
                                                        <CustomPlanConfigurator
                                                            projects={customPlanParams.projects}
                                                            keywords={customPlanParams.keywords}
                                                            bundles={customPlanParams.bundles}
                                                            chats={customPlanParams.chats}
                                                            minKeywords={projectParams.keywords}
                                                            minBundles={projectParams.bundles}
                                                            minChats={projectParams.chats}
                                                            onProjectsChange={(value) => {
                                                                setCustomPlanParams((prev) => ({
                                                                    ...prev,
                                                                    projects: Math.max(1, value),
                                                                }));
                                                            }}
                                                            onKeywordsChange={(value) => {
                                                                setCustomPlanParams((prev) => ({
                                                                    ...prev,
                                                                    keywords: Math.max(projectParams.keywords, value),
                                                                }));
                                                            }}
                                                            onBundlesChange={(value) => {
                                                                setCustomPlanParams((prev) => ({
                                                                    ...prev,
                                                                    bundles: Math.max(projectParams.bundles, value),
                                                                }));
                                                            }}
                                                            onChatsChange={(value) => {
                                                                setCustomPlanParams((prev) => ({
                                                                    ...prev,
                                                                    chats: Math.max(projectParams.chats, value),
                                                                }));
                                                            }}
                                                            aiAnalysisEnabled={customPlanAIAnalysis}
                                                            onAIAnalysisChange={setCustomPlanAIAnalysis}
                                                            aiAnalysisPrice={customPlanSettings.ai_analysis_price}
                                                            leadsExportEnabled={customPlanLeadsExport}
                                                            onLeadsExportChange={setCustomPlanLeadsExport}
                                                            projectPrice={customPlanSettings.project_price}
                                                            keywordPrice={customPlanSettings.keyword_price}
                                                            bundlePrice={customPlanSettings.bundle_price}
                                                            chatPrice={customPlanSettings.chat_price}
                                                            basePrice={customPlanSettings.base_price}
                                                        />
                                                    ) : null
                                                }
                                                onChange={(val) => {
                                                    const planId = Number(val);
                                                    console.log(
                                                        'Индивидуальный тариф выбран, val:',
                                                        val,
                                                        'planId:',
                                                        planId,
                                                        'CUSTOM_PLAN_ID:',
                                                        CUSTOM_PLAN_ID
                                                    );
                                                    setSelectedTariff(planId);
                                                    handlePlanChange(planId);
                                                }}
                                            />
                                        </Box>
                                    )}

                                    {/* Карточка "Результат" под индивидуальным тарифом */}
                                    {shouldShowCustomPlan && selectedPlan === CUSTOM_PLAN_ID && (
                                        <Box
                                            sx={{
                                                gridColumn: isMobile ? '1' : '1 / -1',
                                                p: 2.5,
                                                backgroundColor: 'rgba(0, 68, 255, 0.05)',
                                                borderRadius: 2,
                                                border: '2px solid rgba(0, 68, 255, 0.2)',
                                                mt: 2,
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontSize: 16,
                                                    fontWeight: 600,
                                                    mb: 1,
                                                    color: '#000',
                                                }}
                                            >
                                                📊 Потенциальный результат:
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontSize: 15,
                                                    fontWeight: 400,
                                                    color: 'rgba(0, 0, 0, 0.7)',
                                                    lineHeight: 1.5,
                                                }}
                                            >
                                                🚀 Количество лидов зависит от выбранных параметров мониторинга
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* Карточка "Результат" для выбранного тарифа (не индивидуального) */}
                                    {selectedPlan !== CUSTOM_PLAN_ID &&
                                        subscription &&
                                        subscription.potential_clients_per_month &&
                                        subscription.potential_clients_per_month > 0 && (
                                            <Box
                                                sx={{
                                                    gridColumn: isMobile ? '1' : '1 / -1',
                                                    p: 2.5,
                                                    backgroundColor: 'rgba(0, 68, 255, 0.05)',
                                                    borderRadius: 2,
                                                    border: '2px solid rgba(0, 68, 255, 0.2)',
                                                    mt: 2,
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize: 16,
                                                        fontWeight: 600,
                                                        mb: 1,
                                                        color: '#000',
                                                    }}
                                                >
                                                    📊 Потенциальный результат:
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        fontSize: 15,
                                                        fontWeight: 400,
                                                        color: 'rgba(0, 0, 0, 0.7)',
                                                        lineHeight: 1.5,
                                                    }}
                                                >
                                                    🚀 ~{subscription.potential_clients_per_month} целевых лидов / мес
                                                </Typography>
                                            </Box>
                                        )}
                                </Box>
                            </Box>

                            {/* Визуальный разделитель перед сроком тарифа */}
                            <Box
                                sx={{
                                    height: '1px',
                                    backgroundColor: '#E3ECF3',
                                    my: 4,
                                    width: '100%',
                                }}
                            />

                            {/* Срок тарифа - размещен после выбора тарифа */}
                            <Box mb={4}>
                                <Typography
                                    variant="h5"
                                    color="primary"
                                    sx={{
                                        fontWeight: 800,
                                        fontSize: 24,
                                        mb: 2.5,
                                        lineHeight: 1.2,
                                    }}
                                >
                                    Срок тарифа
                                </Typography>
                                <PeriodField
                                    periods={periods}
                                    control={control}
                                    name="duration_months"
                                />
                            </Box>

                            {/* Визуальный разделитель */}
                            <Box
                                sx={{
                                    height: '1px',
                                    backgroundColor: '#E3ECF3',
                                    my: 4,
                                    width: '100%',
                                }}
                            />

                            {/* Настройки оплаты */}
                            <Box mb={4}>
                                <Typography
                                    variant="h5"
                                    color="primary"
                                    sx={{
                                        fontWeight: 800,
                                        fontSize: 24,
                                        mb: 2.5,
                                        lineHeight: 1.2,
                                    }}
                                >
                                    Настройки оплаты
                                </Typography>

                                <Box mb={2.5}>
                                    <Points
                                        name="bonus"
                                        control={control}
                                        points={bonusPoints}
                                    />
                                </Box>

                                <Box mb={2.5}>
                                    <TicketField
                                        name="promo_code"
                                        control={control}
                                        placeholder="Промокод"
                                    />
                                </Box>
                            </Box>

                            {/* Визуальный разделитель */}
                            <Box
                                sx={{
                                    height: '1px',
                                    backgroundColor: '#E3ECF3',
                                    my: 4,
                                    width: '100%',
                                }}
                            />

                            {/* Итоговая сумма */}
                            <Box mb={3}>
                                <Box
                                    border="1px solid #E3ECF3"
                                    bgcolor="#fff"
                                    borderRadius="16px"
                                    sx={{ overflow: 'hidden' }}
                                >
                                    {isUseBonus && actualBonusToUse > 0 && (
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            px={2}
                                            py={1}
                                        >
                                            <Typography
                                                color="primary"
                                                variant="body2"
                                            >
                                                Списано бонусов
                                            </Typography>
                                            <Typography
                                                color="primary"
                                                variant="body2"
                                            >
                                                -{formatNumber(Math.round(actualBonusToUse))} ₽
                                            </Typography>
                                        </Box>
                                    )}
                                    {promoSale > 0 && (
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            px={2}
                                            py={1}
                                        >
                                            <Typography
                                                color="primary"
                                                variant="body2"
                                            >
                                                Скидка по промокоду
                                            </Typography>
                                            <Typography
                                                color="primary"
                                                variant="body2"
                                            >
                                                -{formatNumber(Math.round(promocodeDiff))} ₽
                                            </Typography>
                                        </Box>
                                    )}
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        px={2}
                                        py={2}
                                        sx={{
                                            borderTop: '1px solid #E3ECF3',
                                        }}
                                    >
                                        <Typography
                                            color="primary"
                                            variant="h6"
                                            sx={{
                                                fontWeight: 800,
                                                fontSize: 20,
                                            }}
                                        >
                                            Итого
                                        </Typography>
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            gap={1}
                                        >
                                            {subscription && total < subscription.price * periodId && (
                                                <Typography
                                                    variant="body1"
                                                    color="text.disabled"
                                                    sx={{ textDecoration: 'line-through', opacity: 0.7 }}
                                                >
                                                    {formatNumber(subscription.price * periodId)} ₽
                                                </Typography>
                                            )}
                                            <Typography
                                                color="primary"
                                                variant="h6"
                                                sx={{
                                                    fontWeight: 800,
                                                    fontSize: 20,
                                                }}
                                            >
                                                {formatNumber(total)} ₽
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>

                            {/* Автоматическое продление */}
                            <Box
                                mt={4}
                                mb={3}
                                sx={{
                                    p: 2.5,
                                    border: '1px solid #E3ECF3',
                                    borderRadius: '16px',
                                    backgroundColor: '#fff',
                                }}
                            >
                                <FormControlLabel
                                    control={
                                        <Controller
                                            name="auto_renewal"
                                            control={control}
                                            render={({ field }) => (
                                                <Switch
                                                    checked={field.value}
                                                    onChange={field.onChange}
                                                    color="primary"
                                                />
                                            )}
                                        />
                                    }
                                    label={
                                        <Box>
                                            <Typography
                                                variant="body1"
                                                color="primary"
                                                sx={{ fontWeight: 600, mb: 0.5 }}
                                            >
                                                Автоматическое продление тарифа
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{ color: 'rgba(0, 0, 0, 0.6)' }}
                                            >
                                                При включении автопродления вы получите 10% бонусами сразу после оплаты
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </Box>

                            {watch('auto_renewal') && (
                                <Box
                                    mt={2}
                                    mb={3}
                                >
                                    <Box
                                        sx={{
                                            position: 'relative',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                backgroundColor: '#4AE287',
                                                opacity: 0.2,
                                                borderRadius: 4,
                                            }}
                                        />
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            px={2}
                                            py={1}
                                        >
                                            <Typography
                                                sx={{
                                                    color: '#000000',
                                                    fontWeight: 500,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }}
                                                variant="body1"
                                            >
                                                💸 Вы получите {formatNumber(Math.round(total * 0.1))} ₽ бонусами сразу
                                                после оплаты
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            )}

                            <Box mt={3}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    size="large"
                                    fullWidth
                                    disabled={isCreatePaymentLoading}
                                >
                                    Перейти к оплате
                                </Button>
                                {watch('auto_renewal') && (
                                    <Typography
                                        variant="caption"
                                        color="text.disabled"
                                        sx={{ display: 'block', mt: 1, textAlign: 'center' }}
                                    >
                                        Автопродление можно будет отключить в разделе «Ваш тариф»
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </form>
                </Box>
            </LoadingWrapper>
        );
    };

    if (!isMobile) {
        return <DesktopLayout>{renderContent()}</DesktopLayout>;
    }

    return <WithoutHeader showBack>{renderContent()}</WithoutHeader>;
}
